import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Layer } from '../../../../models/layer.models';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, Subject, take } from 'rxjs';
import { LayerService } from '../../../../services/layer.service';
import { MapCommunicatorService } from '../../map-communicator.service';
import { getGradientBackground, isLinearLayer } from 'src/app/utils';
import { NgClass } from '@angular/common';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { InlineSvgComponent } from '../../../../components/inline-svg/inline-svg.component';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { GradientSliderComponent } from '../../../../components/gradient-slider/gradient-slider.component';
import { GradientSelectorComponent } from '../../../../components/gradient-selector/gradient-selector.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { LayersHelpDialogComponent } from '../../../../components/layers-help-dialog/layers-help-dialog.component';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'frmg-layer-sidebar',
  templateUrl: './layer-sidebar.component.html',
  styleUrls: ['./layer-sidebar.component.scss'],
  imports: [NgClass, ReactiveFormsModule, FormsModule, CdkDropList, CdkDrag, CdkDragHandle, InlineSvgComponent, MatSlideToggle, GradientSliderComponent, GradientSelectorComponent, MatCheckbox, MatTooltip]
})
export class LayerSidebarComponent implements OnInit {
  layers: Array<Layer>;
  activeTab = 'basemap';
  selectedBasemap = 'topographic';
  public layersGroup: FormGroup;
  @Input() mapCommunicator!: Subject<string>;
  protected readonly isLinearLayer = isLinearLayer;
  public gradients = {};


  constructor(private fb: FormBuilder,
              private mapCommunicatorService: MapCommunicatorService,
              private layerService: LayerService,
              private cd: ChangeDetectorRef,
              private dialog: MatDialog) {
    this.layersGroup = this.fb.group({});
  }

  ngOnInit() {
    this.fetchLayers();
    if (localStorage.getItem('basemap') !== this.selectedBasemap) {
      this.selectedBasemap = localStorage.getItem('basemap') || 'topographic';
      this.activeTab = localStorage.getItem('activeLayersTab') || 'basemap';
      this.onBasemapChange();
    }
    this.activeTab = localStorage.getItem('activeLayersTab') || 'basemap';
  }

  public getActiveTab(tab: string) {
    localStorage.setItem('activeLayersTab', tab);
    this.activeTab = tab;
  }


  fetchLayers(): void {
    this.layerService.getLayers().pipe(debounceTime(0), take(1)).subscribe((response: any) => {
      const layers = response.items.map((layer: any) => ({
        layer: layer.layer,
        settings: layer.settings,
        enabled: false,
        order: 0,
        active: false
      }));
      layers.forEach((layer: Layer & { 'enabled', 'order', 'selected' }, index: number) => {
        this.gradients[layer.layer.name] = getGradientBackground(layer);
        this.layersGroup.addControl(layer.layer.name, this.fb.group({
          active: [false],
          order: [index],
          range: this.fb.control({
            start: layer.layer.min_threshold,
            end: layer.layer.max_threshold,
            invert: false
          }),
          selected: [Object.keys(layer.layer.colormap_values)],
          layer
        }));
      });
      this.layers = layers;
      this.cd.detectChanges();
      this.layersGroup.valueChanges.subscribe(layers => {
        this.layerService.setSavedLayers(layers);
        this.mapCommunicatorService.layers.next(Object.values(layers));
      });
      this.layersGroup.patchValue(this.layerService.getSavedLayers(), { emitEvent: true });
    });
  }

  public isSelected(layerName: string) {
    return this.layersGroup.get(layerName)?.get('range')?.value?.invert;
  }

  public invertLayer(layerName: string) {
    const range = this.layersGroup.get(layerName)?.get('range')?.value;
    range.invert = !range.invert;
    this.layersGroup.get(layerName)?.get('range')?.setValue(range, { emitEvent: true });
  }

  isNumeric(value: string): boolean {
    return !isNaN(parseFloat(value));
  }

  getDiscreteColors(colorMap: any): string[] {
    return Object.values(colorMap);
  }

  getUnitSymbol(unit: string): string {
    if (!unit) {
      return '';
    }
    let symbol = '';
    switch (unit.toLowerCase()) {
      case 'percentage':
        symbol = '%';
        break;
      case 'meters':
        symbol = 'm';
        break;
      case 'kilometers':
        symbol = 'km';
        break;
      case 'MILES':
        symbol = 'mi';
        break;
      default:
        symbol = unit;
        break;
    }
    return ` (${symbol})`;
  }

  getThumbImage(layer_name: string) {
    let file_name = '';
    switch (layer_name) {
      case 'Canopy Height':
        file_name = 'chm';
        break;
      case 'Land Cover':
        file_name = 'landcover';
        break;
      case 'Fuel Hazard':
        file_name = 'fuelhazard';
        break;
      case 'Exposure':
        file_name = 'exposure';
        break;
      case 'Fuel Type':
        file_name = 'fueltype';
        break;
      case 'Percent Conifer Basal Area':
        file_name = 'pcba';
        break;
      case 'Distance To Hazard':
        file_name = 'disttohazard';
        break;
      default:
        file_name = layer_name.toLowerCase();
        break;
    }
    return `assets/images/layers-thumbs/${file_name}.png`;
  }

  toggleActiveLayer(layerName: string) {
    const active = !this.layersGroup.get(layerName)?.get('active')?.value;
    this.layersGroup.get(layerName)?.get('active')?.setValue(active, { emitEvent: true });
  }


  onWheel(event: WheelEvent): void {
    const container = event.currentTarget as HTMLElement;
    container.scrollBy(0, event.deltaY);
    event.preventDefault();
  }

  onBasemapChange(): void {
    this.mapCommunicatorService.mapCommunicator.next(`basemap_${this.selectedBasemap}`);
    this.mapCommunicatorService.baseMap = this.selectedBasemap;
    localStorage.setItem('basemap', this.selectedBasemap);
  }

  drop(event: CdkDragDrop<string[]>) {
    const layersGroupPatch = {};
    this.moveAndReindex(this.layers, event.previousIndex, event.currentIndex).forEach((layer, index) => {
      layersGroupPatch[layer.layer.name] = { order: index };
    });
    this.layersGroup.patchValue(layersGroupPatch, { emitEvent: true });
    moveItemInArray(this.layers, event.previousIndex, event.currentIndex);
    this.reorderServer(this.layers[event.currentIndex], event.currentIndex);
  }

  private reorderServer(layer: Layer, index: number) {
    let after_layer_id = null;
    let before_layer_id = null;
    if (index !== 0) {
      after_layer_id = this.layers[index - 1].layer.id;
    }
    if (index !== this.layers.length - 1) {
      before_layer_id = this.layers[index + 1].layer.id;
    }
    this.layerService.reorderLayer(layer.layer.id, {
      after_layer_id,
      before_layer_id
    }).subscribe(() => {
    }, _ => {
    });
  }


  private moveAndReindex(
    list: any[],
    from: number,
    to: number
  ) {
    if (from === to) return list.map((it, i) => ({ ...it, order: i }));

    const copy = list.slice();
    const [moved] = copy.splice(from, 1);
    copy.splice(to, 0, moved);

    return copy.map((item, idx) => ({ ...item, order: idx }));
  }

  hideCheckboxPCBALandcover(name: string) {
    if (name === 'pcba') {
      return !this.layersGroup.get('landcover')?.value.active;
    } else if (name === 'landcover') {
      return !this.layersGroup.get('pcba')?.value.active;
    } else {
      return true;
    }
  }

  formatLegend(legend: string, name?: string): string {
    const value: number = parseFloat(legend);
    if (name === 'pcba' && value === 0) {
      return 'Deciduous (0%)';
    } else if (name === 'pcba' && value === 100) {
      return 'Conifer (100%)';
    } else if (name === 'pcba' && value === 50) {
      return '';
    }
    if (!isNaN(value)) {
      return value.toFixed(0);
      // return value >= 1000 ? (value / 1000).toFixed(1) + 'k' : value.toFixed(0);
    }
    return legend;
  }

  getSpacedNumericLegends(layer: Layer): string[] {
    const numericLegends = Object.keys(layer.layer.colormap_values);
    const min = layer.layer.min_threshold;
    const max = layer.layer.max_threshold;
    const step = (max - min) / (numericLegends.length - 1);
    // distance to hazard fix
    if (max === 2000) {
      const length = numericLegends.length;
      return [`${numericLegends[0]}`, `${numericLegends[length - 1]}`];
    }

    return Array.from({ length: numericLegends.length }, (_, i) => (min + i * step).toFixed(0));
  }

  openHelpModal() {
    this.dialog.open<LayersHelpDialogComponent>(LayersHelpDialogComponent, {
      width: '640px'
    });
  }
}
